import React, { useRef, useState } from 'react';
import { Box, Button, Chip, IconButton, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { showToastMessage } from '../../../utils/AppUtils';
import { BRAND_LUXE_PRIMARY, BRAND_SURFACE_CONTAINER_HIGHEST, BRAND_OUTLINE_VARIANT } from '../brandDashboardTokens';

export interface AssetEntry {
    file?: File;
    url: string;
    description: string;
    requirements: string[];
}

interface Props {
    assets: AssetEntry[];
    onChange: (assets: AssetEntry[]) => void;
}

const ACCEPT = '.zip,application/zip,application/x-zip-compressed';

function isZipFile(file: File): boolean {
    const name = file.name.toLowerCase();
    return name.endsWith('.zip') || file.type === 'application/zip' || file.type === 'application/x-zip-compressed';
}

function formatSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
        borderRadius: '10px',
        color: '#fff',
        fontSize: 14,
        '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.22)' },
        '&.Mui-focused fieldset': { borderColor: BRAND_LUXE_PRIMARY, borderWidth: 1 },
    },
    '& .MuiInputBase-input': { color: '#fff', py: '10px', px: '14px' },
    '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
};

export default function CampaignAssetsEditor({ assets, onChange }: Props) {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const zips = files.filter(isZipFile);
        if (zips.length < files.length) {
            showToastMessage('Only ZIP files are supported for campaign assets', 'warning');
        }
        if (!zips.length) {
            e.target.value = '';
            return;
        }
        const newEntries: AssetEntry[] = zips.map(f => ({
            file: f,
            url: URL.createObjectURL(f),
            description: '',
            requirements: [],
        }));
        onChange([...assets, ...newEntries]);
        e.target.value = '';
    };

    const remove = (idx: number) => onChange(assets.filter((_, i) => i !== idx));

    const update = (idx: number, patch: Partial<AssetEntry>) => {
        onChange(assets.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <input ref={inputRef} type="file" accept={ACCEPT} multiple hidden onChange={handleFileSelect} />

            {assets.map((asset, idx) => (
                <AssetCard
                    key={idx}
                    asset={asset}
                    onRemove={() => remove(idx)}
                    onUpdate={patch => update(idx, patch)}
                />
            ))}

            <Button
                onClick={() => inputRef.current?.click()}
                startIcon={<AddIcon />}
                sx={{
                    py: 2,
                    borderRadius: '12px',
                    border: `2px dashed ${BRAND_OUTLINE_VARIANT}50`,
                    color: 'rgba(255,255,255,0.5)',
                    textTransform: 'none',
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 13,
                    transition: 'all 0.2s',
                    '&:hover': {
                        borderColor: `${BRAND_LUXE_PRIMARY}60`,
                        color: BRAND_LUXE_PRIMARY,
                        bgcolor: 'rgba(255,201,101,0.04)',
                    },
                }}>
                Upload ZIP
            </Button>
        </Box>
    );
}

function AssetCard({
    asset,
    onRemove,
    onUpdate,
}: {
    asset: AssetEntry;
    onRemove: () => void;
    onUpdate: (patch: Partial<AssetEntry>) => void;
}) {
    const [reqInput, setReqInput] = useState('');
    const fileName = asset.file?.name || asset.url.split('/').pop() || 'asset.zip';
    const fileSize = asset.file ? formatSize(asset.file.size) : null;

    const addReq = () => {
        const val = reqInput.trim();
        if (!val) return;
        onUpdate({ requirements: [...asset.requirements, val] });
        setReqInput('');
    };

    const removeReq = (i: number) => {
        onUpdate({ requirements: asset.requirements.filter((_, idx) => idx !== i) });
    };

    return (
        <Box
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                borderRadius: '12px',
                border: `1px solid rgba(255,255,255,0.06)`,
                overflow: 'hidden',
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '8px',
                        bgcolor: 'rgba(255,255,255,0.06)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: BRAND_LUXE_PRIMARY,
                        flexShrink: 0,
                    }}>
                    <FolderZipOutlinedIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: '#fff' }}>
                        {fileName}
                    </Typography>
                    {fileSize && (
                        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{fileSize}</Typography>
                    )}
                </Box>
                <IconButton
                    onClick={onRemove}
                    size="small"
                    sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#ff7351' } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField
                    fullWidth
                    placeholder="Description (optional)"
                    value={asset.description}
                    onChange={e => onUpdate({ description: e.target.value })}
                    size="small"
                    sx={fieldSx}
                />

                {asset.requirements.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {asset.requirements.map((req, i) => (
                            <Chip
                                key={i}
                                label={req}
                                size="small"
                                onDelete={() => removeReq(i)}
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 12,
                                    bgcolor: 'rgba(255,201,101,0.1)',
                                    color: BRAND_LUXE_PRIMARY,
                                    '& .MuiChip-deleteIcon': {
                                        color: 'rgba(255,201,101,0.4)',
                                        '&:hover': { color: BRAND_LUXE_PRIMARY },
                                    },
                                }}
                            />
                        ))}
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        fullWidth
                        placeholder="Add requirement and press Enter"
                        value={reqInput}
                        onChange={e => setReqInput(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                addReq();
                            }
                        }}
                        size="small"
                        sx={fieldSx}
                    />
                    <IconButton
                        onClick={addReq}
                        disabled={!reqInput.trim()}
                        size="small"
                        sx={{
                            color: 'rgba(255,255,255,0.3)',
                            '&:hover': { color: BRAND_LUXE_PRIMARY },
                            ':disabled': { color: 'rgba(255,255,255,0.1)' },
                        }}>
                        <AddIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
}
